import { useCenterStore } from '@caradasstore/CenterStore'
import { useOrganisationStore } from '@caradasstore/OrganisationStore'
import { SchedularCalendarEvent } from '@components/scheduler/schedule-calendar-event'
import { AdasBox, AdasCalendar, AdasDatePicker, AdasTextField, AdsCustomModal } from '@components/wrapper-components'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { getDateOverrides, getEvents, getReferralList, getScheduleAvailability } from 'api/api'
import { DATE_FORMATS } from 'constants'
import dayjs from 'dayjs'
import { WeekdaysEnum, weekdaysEnumToFCIndex } from 'enums/enums'
import { Logger } from 'logger'
import PropTypes from 'prop-types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getTimeZoneByCode, mapEventsToCalendarFormat, mapOverrideToCalendarFormat } from 'utils/utils'
import './SchedulerCalendar.css'
import 'dayjs/locale/en-gb'
import { useDeviceQueries } from '@hooks/useDeviceQueries'
import { daysOfWeekMap } from 'utils/utils'

const ViewToggle = ({ value, onChange, count }) => {
  const opts = [
    { key: 'timeGridDay', label: 'Day' },
    { key: 'timeGridWeek', label: 'Week' }
  ]
  return (
    <div className='inline-flex h-10 items-center rounded-lg bg-[#E5E7EB] p-1'>
      {opts.map((o) => {
        const active = o.key === value
        return (
          <button
            key={o.key}
            type='button'
            onClick={() => onChange(o.key)}
            className={`inline-flex h-8 items-center gap-1.5 rounded-md px-4 text-[12px] font-bold uppercase tracking-wide transition-colors ${
              active ? 'bg-white text-[#0A1628] shadow-sm' : 'text-[#6B7280] hover:text-[#0A1628]'
            }`}
          >
            {o.label}
            {active && count > 0 && (
              <span className='inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[#DBEAFE] px-1.5 text-[11px] font-semibold text-[#1F6FEB]'>
                {count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

ViewToggle.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  count: PropTypes.number
}

export const ScheduleCalendar = ({ initialView, refresh, openNewEventSlider, onCountChange, onEventOpen }) => {
  const calendarRef = useRef(null)
  const ccId = useCenterStore((store) => store.ccId)
  const organisationId = useOrganisationStore((store) => store.organisationId)
  const organisationDetails = useOrganisationStore((state) => state.organisationDetail)
  const [view, setView] = useState(initialView)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState([])
  const [calendarHiddenDays, setCalendarHiddenDays] = useState([])
  const [unavailableDates, setUnavailableDates] = useState([])
  const { isMobile, isLandscape } = useDeviceQueries()
  const [businessHours, setBusinessHours] = useState()
  const [referralSourceList, setReferralSourceList] = useState([])
  const [searchInput, setSearchInput] = useState('')

  useEffect(() => {
    const fetchReferralSource = async () => {
      const referralListResponse = await getReferralList(ccId)
      if (referralListResponse?.status === 200) {
        const formattedReferralList = referralListResponse.data
          .sort((a, b) => a.company.localeCompare(b.company))
          .map((referralObj) => ({ id: referralObj.code, value: referralObj.company }))
        setReferralSourceList(formattedReferralList)
      }
    }
    fetchReferralSource()
  }, [])

  useEffect(() => {
    setView(isMobile && !isLandscape ? 'timeGridDay' : initialView)
    fetchEvents(isMobile && !isLandscape ? 'timeGridDay' : initialView)
  }, [isMobile, isLandscape, initialView])

  const updateTitle = () => {}

  const handleViewChange = (nextView) => {
    if (!nextView || nextView === view) return
    setView(nextView)
    // Clear the previous view's events synchronously so the count chip doesn't
    // briefly show the wrong number while the next fetch is in flight.
    setSelectedEvent([])
    fetchEvents(nextView)
    updateTitle()
  }

  const handleTodayClick = () => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi()
      calendarApi.today()
      const today = calendarApi.getDate()
      setSelectedDate(new Date())
      fetchEvents(view, today)
      updateTitle()
    }
  }

  const handlePrevClick = () => {
    if (!calendarRef.current) return
    const calendarApi = calendarRef.current.getApi()
    calendarApi.prev()
    const newDate = view === 'timeGridWeek' ? dayjs(selectedDate).subtract(1, 'week').toDate() : calendarApi.getDate()
    setSelectedDate(newDate)
    fetchEvents(view, newDate)
    updateTitle()
  }

  const handleNextClick = () => {
    if (!calendarRef.current) return
    const calendarApi = calendarRef.current.getApi()
    calendarApi.next()
    const newDate = view === 'timeGridWeek' ? dayjs(selectedDate).add(1, 'week').toDate() : calendarApi.getDate()
    setSelectedDate(newDate)
    fetchEvents(view, newDate)
    updateTitle()
  }

  useEffect(() => {
    updateTitle()
  }, [selectedDate])

  const handleDateChange = (newDate) => {
    if (newDate && dayjs(newDate).isValid()) {
      setSelectedDate(newDate)
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi()
        calendarApi.gotoDate(dayjs(newDate).format(DATE_FORMATS.ISO_DATE))
        const formattedDate = dayjs(newDate).format(DATE_FORMATS.ISO_DATE)
        fetchEvents(view, formattedDate)
        updateTitle()
      }
      setIsDatePickerOpen(false)
    }
  }

  const fetchEvents = async (viewMode, date) => {
    let startDate, endDate
    const referenceDate = date || selectedDate
    if (viewMode === 'timeGridDay') {
      startDate = dayjs(referenceDate).startOf('day').utc(false).format(DATE_FORMATS.ISO_DATE)
      endDate = dayjs(referenceDate).endOf('day').utc(false).format(DATE_FORMATS.ISO_DATE)
    } else if (viewMode === 'timeGridWeek') {
      const dayOfWeek = dayjs(referenceDate).day()
      const daysToMonday = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek
      startDate = dayjs(referenceDate).add(daysToMonday, 'day').format(DATE_FORMATS.ISO_DATE)
      endDate = dayjs(startDate).add(6, 'day').format(DATE_FORMATS.ISO_DATE)
    }

    const params = { ccId, organisationId, startDate, endDate, searchTerm: searchInput }

    try {
      const response = await getEvents(params)
      if (response && response.status === 200) {
        setSelectedEvent(mapEventsToCalendarFormat(response.data))
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'scheduleCalendar', method: 'fetchEvents' } })
    }
  }

  const getFullCalenderHiddenDaysFromAvailability = (fetchedAvailability) => {
    return fetchedAvailability
      .filter((dayObj) => {
        const { start_time, end_time, day } = dayObj
        return (
          start_time[0] === 0 && start_time[1] === 0 && end_time[0] === 0 && end_time[1] === 0 && (day === 'SATURDAY' || day === 'SUNDAY')
        )
      })
      .map((dayObj) => {
        const dayEnumValue = WeekdaysEnum[dayObj.day]
        return weekdaysEnumToFCIndex[dayEnumValue]
      })
  }

  const mapBusinessHours = (data) => {
    return data
      .filter((day) => !(day.start_time[0] === 0 && day.end_time[0] === 0))
      .map((day) => ({
        daysOfWeek: [daysOfWeekMap[day.day]],
        startTime: `${day.start_time[0].toString().padStart(2, '0')}:${day.start_time[1].toString().padStart(2, '0')}`,
        endTime: `${day.end_time[0].toString().padStart(2, '0')}:${day.end_time[1].toString().padStart(2, '0')}`
      }))
  }

  const fetchAndSetFullCalendarHiddenDays = async () => {
    try {
      const response = await getScheduleAvailability({ ccId, organisation_id: organisationId })
      const fetchedAvailability = response?.data
      if (fetchedAvailability && fetchedAvailability.length > 0) {
        setCalendarHiddenDays(getFullCalenderHiddenDaysFromAvailability(fetchedAvailability))
        setBusinessHours(mapBusinessHours(fetchedAvailability))
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'scheduleCalendar', method: 'fetchAndSetFullCalendarHiddenDays' } })
    }
  }

  const fetchDateOverrides = async () => {
    try {
      const response = await getDateOverrides({ ccId, organisation_id: organisationId })
      if (response) {
        setUnavailableDates(mapOverrideToCalendarFormat(response.data, getTimeZoneByCode(organisationDetails.timezone_at)))
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'dateoverrides', method: 'fetchDateOverrides' } })
    }
  }

  useEffect(() => {
    if (ccId && organisationId) {
      fetchEvents(view)
      fetchAndSetFullCalendarHiddenDays()
      fetchDateOverrides()
    }
  }, [ccId, organisationId, refresh, searchInput])

  const handleEventClick = (eventInfo) => {
    if (eventInfo?.event?.extendedProps?.override) return
    if (eventInfo?.event?.id && onEventOpen) onEventOpen(eventInfo.event.id)
  }

  const isAllowEventSelection = (eventInfo) => {
    const eventDateTime = dayjs(eventInfo.startStr)
    const startOfCurrentDate = dayjs().startOf('day')
    const futureLimit = startOfCurrentDate.add(organisationDetails?.max_time, 'minute')
    return eventDateTime.isAfter(startOfCurrentDate) && eventDateTime.isBefore(futureLimit)
  }

  useEffect(() => {
    if (onCountChange) onCountChange(selectedEvent.length, selectedEvent.length)
  }, [selectedEvent, onCountChange])

  const dateRangeLabel = useMemo(() => {
    const d = dayjs(selectedDate)
    if (view === 'timeGridDay') return d.format('MMMM D, YYYY')
    const dayOfWeek = d.day()
    const daysToMonday = (dayOfWeek === 0 ? -6 : 1) - dayOfWeek
    const start = d.add(daysToMonday, 'day')
    const end = start.add(6, 'day')
    if (start.month() === end.month()) {
      return `${start.format('MMMM D')} — ${end.format('D, YYYY')}`
    }
    return `${start.format('MMM D')} — ${end.format('MMM D, YYYY')}`
  }, [selectedDate, view])

  const renderEventContent = (eventInfo) => {
    const { title, extendedProps } = eventInfo.event
    const { id, type, assignee, start, client, offset, vehicle, duration, override } = extendedProps
    const timeText = override ? '' : dayjs(start).tz(offset).format(DATE_FORMATS.ISO_TIME_STRING)
    const repair_order_num = (duration === 60 && !assignee?.full_name) || duration >= 90 ? vehicle.repair_order_num : ''
    return (
      <SchedularCalendarEvent
        override={override}
        timeText={timeText}
        type={type}
        title={title}
        id={id}
        assingeeName={assignee?.full_name || ''}
        isActive={assignee?.is_active || ''}
        fullName={client?.business_name || client?.full_name || ''}
        vehicleYear={vehicle?.year || ''}
        vehicleMake={vehicle?.make || ''}
        repair_order_num={repair_order_num}
        duration={duration}
        referralSource={referralSourceList.find((referral) => referral.id === client?.referral_source)?.value}
      />
    )
  }

  const renderHeaderContent = (date) => {
    const currentDate = dayjs(date.date).utc(false)
    const isToday = date.isToday
    const formattedDate = currentDate.format('ddd').toUpperCase()
    const dayOfMonth = currentDate.format('D')
    return (
      <div className='flex flex-col items-center gap-1 py-2'>
        <span className={`text-[11px] font-semibold uppercase tracking-wide ${isToday ? 'text-[#0A1628]' : 'text-[#6B7280]'}`}>
          {formattedDate}
        </span>
        {isToday ? (
          <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#0A1628] text-[14px] font-bold text-white'>
            {dayOfMonth}
          </span>
        ) : (
          <span className='text-[16px] font-semibold text-[#0A1628]'>{dayOfMonth}</span>
        )}
      </div>
    )
  }

  return (
    <>
      <AdasBox className='mb-4 flex flex-wrap items-center gap-3'>
        <div className='relative flex-1 min-w-[220px] max-w-[340px]'>
          <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder='Search appointments, RO, VIN...'
            className='w-full h-10 pl-9 pr-3 rounded-lg border border-[#D1D5D8] bg-white text-[13px] text-[#0A1628] placeholder-[#9CA3AF] focus:outline-none focus:border-[#1F6FEB]'
          />
        </div>
        <ViewToggle value={view} onChange={handleViewChange} count={selectedEvent.length} />
        <div className='ml-auto flex items-center gap-3'>
          <button
            type='button'
            onClick={handlePrevClick}
            aria-label='Previous'
            className='inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#D1D5D8] bg-white text-[#0A1628] hover:bg-[#F8FAFC]'
          >
            <ChevronLeft className='h-4 w-4' />
          </button>
          <button
            type='button'
            onClick={handleTodayClick}
            className='inline-flex h-8 items-center px-4 rounded-md border border-[#D1D5D8] bg-white text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC]'
          >
            Today
          </button>
          <button
            type='button'
            onClick={handleNextClick}
            aria-label='Next'
            className='inline-flex h-8 w-8 items-center justify-center rounded-md border border-[#D1D5D8] bg-white text-[#0A1628] hover:bg-[#F8FAFC]'
          >
            <ChevronRight className='h-4 w-4' />
          </button>
          <span className='ml-2 text-[14px] font-bold text-[#0A1628] whitespace-nowrap'>{dateRangeLabel}</span>
        </div>
      </AdasBox>

      <AdasCalendar
        view={view}
        height={900}
        SchedularCalendarEvent={SchedularCalendarEvent}
        calendarRef={calendarRef}
        updateTitle={updateTitle}
        setSelectedEvent={setSelectedEvent}
        scrollTime={'07:00'}
        hiddenDays={calendarHiddenDays}
        eventContent={renderEventContent}
        events={[...selectedEvent, ...unavailableDates]}
        nowIndicator={true}
        slotHeight='48px'
        slotMinTime='00:00:00'
        slotMaxTime='24:00:00'
        allDaySlot={false}
        timeZone={getTimeZoneByCode(organisationDetails.timezone_at)}
        eventClick={handleEventClick}
        selectable={true}
        select={openNewEventSlider}
        firstDay={1}
        selectOverlap={true}
        selectAllow={isAllowEventSelection}
        eventMaxStack={3}
        dayHeaderContent={renderHeaderContent}
        className='adas-calendar'
        businessHours={businessHours}
      />

      <AdsCustomModal title='Select Date' open={isDatePickerOpen} onClose={() => setIsDatePickerOpen(false)}>
        <AdasDatePicker
          label='Select Date'
          variant='outlined'
          name='add_date'
          value={dayjs(selectedDate).format(DATE_FORMATS.ISO_DATE_STRING)}
          renderInput={(params) => <AdasTextField {...params} />}
          format={DATE_FORMATS.ISO_DATE_STRING}
          onChange={handleDateChange}
        />
      </AdsCustomModal>
    </>
  )
}

ScheduleCalendar.propTypes = {
  initialView: PropTypes.oneOf(['timeGridDay', 'timeGridWeek']).isRequired,
  refresh: PropTypes.bool,
  openNewEventSlider: PropTypes.func,
  onCountChange: PropTypes.func,
  onEventOpen: PropTypes.func
}
